import { useEffect, useState } from "react";
import { Modal, Table, Button } from "react-bootstrap";
import { Input, InputNumber } from "antd";
import { FaTrashAlt, FaPlus } from "react-icons/fa";
import Swal from "sweetalert2";
import { PutUpdatePlan } from "../../../Service/Service.Plan";
import BuscarProcedimientoEditarModal from "./BuscarProcedimientoEditarModal";
import { calcularMontosCobertura } from "../../../utils/copagoCalculo";

export default function PlanEditarModal({ show, handleClose, planSeleccionado, reload }) {
    const [form, setForm] = useState({
        nombre: "",
        seguro: "",
        detalles: []
    });

    const [copagoGlobal, setCopagoGlobal] = useState(null);
    const [aplicarGlobal, setAplicarGlobal] = useState(false);

    const [showBuscarModal, setShowBuscarModal] = useState(false);

    useEffect(() => {
        if (show && planSeleccionado) {
            setForm({
                nombre: planSeleccionado.nombre,
                seguro: planSeleccionado.Seguros.nombre,
                // COBERTURAS-COPAGO-CALCULO-AUDIT-FIX-01: el reparto es DERIVADO
                // de (copago, tarifa) -- no se muestran precio_paciente/empresa
                // persistidos (los historicos pueden ser inconsistentes).
                detalles: planSeleccionado.detalles.map(d => {
                    const m = calcularMontosCobertura(d.tarifario.precio, d.copago);
                    return {
                        ...d,
                        precio_normal: d.tarifario.precio,
                        precio_empresa: m.precioEmpresa,
                        precio_paciente: m.precioPaciente,
                        copago: d.copago
                    };
                })
            });
        }
    }, [show, planSeleccionado]);

    useEffect(() => {
        if (aplicarGlobal && copagoGlobal !== null) {
            setForm(prev => ({
                ...prev,
                detalles: prev.detalles.map(p => {
                    const m = calcularMontosCobertura(p.precio_normal, parseFloat(copagoGlobal));
                    return {
                        ...p,
                        copago: m.copago,
                        precio_paciente: m.precioPaciente,
                        precio_empresa: m.precioEmpresa
                    };
                })
            }));
        }
    }, [copagoGlobal, aplicarGlobal]);

    const handleChangePrecio = (value, index) => {
        const newDetalles = [...form.detalles];
        const m = calcularMontosCobertura(newDetalles[index].precio_normal, parseFloat(value || 0));
        newDetalles[index].copago = m.copago;
        newDetalles[index].precio_paciente = m.precioPaciente;
        newDetalles[index].precio_empresa = m.precioEmpresa;
        setForm(prev => ({ ...prev, detalles: newDetalles }));
    };

    const handleRemove = (index) => {
        const nuevos = [...form.detalles];
        nuevos.splice(index, 1);
        setForm(prev => ({ ...prev, detalles: nuevos }));
    };

    const handleGuardar = async () => {
        try {
            const payload = {
                cabecera: {
                    nombre: form.nombre,
                    seguro_id: planSeleccionado.Seguros.idseguro
                },
                detalle: form.detalles.map(d => ({
                    id_tipo_examen: d.id_tipo_examen,
                    copago: d.copago,
                    precio_paciente: d.precio_paciente,
                    precio_empresa: d.precio_empresa,
                    idprocedimiento_company_precios: d.idprocedimiento_company_precios
                })),
                eliminar: planSeleccionado.detalles
                    .filter(orig => !form.detalles.some(d => d.idprocedimiento_company_precios === orig.idprocedimiento_company_precios))
                    .map(d => d.idprocedimiento_company_precios)
            };

            const response = await PutUpdatePlan(planSeleccionado.idplan, payload);

            if (!response.success) throw new Error(response.message);

            Swal.fire("¡Actualizado!", response.message, "success");
            handleClose();
            reload();
        } catch (error) {
            Swal.fire("Error", error.message || "Ocurrió un error al actualizar", "error");
        }
    };

    const handleAgregarExamenes = (nuevos) => {
        const detallesActualizados = [
            ...form.detalles,
            ...nuevos.map(p => ({
                ...p,
                id_tipo_examen: p.id_tipo_examen ?? p.ExamenTipo?.idExamenTipo ?? p.idExamenTipo,
                precio_paciente: p.precio_paciente ?? 0,
                precio_empresa: p.precio_empresa ?? p.precio,
                copago: p.copago ?? 0,
                precio_normal: p.precio ?? 0
            }))
        ];
        setForm(prev => ({ ...prev, detalles: detallesActualizados }));
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Editar Plan</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <label>Nombre del Plan</label>
                    <Input value={form.nombre} disabled />
                </div>
                <div className="mb-3">
                    <label>Seguro/Entidad</label>
                    <Input value={form.seguro} disabled />
                </div>

                <Button variant="outline-primary" size="sm" className="mb-2" onClick={() => setShowBuscarModal(true)}>
                    <FaPlus /> Agregar Examen
                </Button>

                <div className="d-flex align-items-center mb-2">
                    <InputNumber
                        min={0}
                        max={100}
                        value={copagoGlobal}
                        onChange={(val) => setCopagoGlobal(val)}
                        placeholder="% Copago general"
                        style={{ marginRight: '10px', width: '120px' }}
                    />
                    <label className="form-check-label d-flex align-items-center">
                        <input
                            type="checkbox"
                            className="form-check-input me-1"
                            checked={aplicarGlobal}
                            onChange={(e) => setAplicarGlobal(e.target.checked)}
                        />
                        Aplicar a todos los exámenes del plan
                    </label>
                </div>

                <Table bordered hover size="sm">
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Descripción</th>
                            <th>Precio Normal</th>
                            <th>Copago %</th>
                            <th>P. Paciente</th>
                            <th>P. Empresa</th>
                            <th>Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {form.detalles.map((item, index) => (
                            <tr key={index}>
                                <td>{item.ExamenTipo.codigo}</td>
                                <td>{item.ExamenTipo.descripcion}</td>
                                <td>${(item.precio_normal ?? 0).toFixed(2)}</td>
                                <td>
                                    <Input
                                        type="number"
                                        value={item.copago}
                                        onChange={(e) => handleChangePrecio(e.target.value, index)}
                                        min={0}
                                        max={100}
                                    />
                                </td>
                                <td>${(item.precio_paciente ?? 0).toFixed(2)}</td>
                                <td>${(item.precio_empresa ?? 0).toFixed(2)}</td>
                                <td>
                                    <Button variant="danger" size="sm" onClick={() => handleRemove(index)}>
                                        <FaTrashAlt />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleGuardar}>
                    Guardar Cambios
                </Button>
            </Modal.Footer>
            <BuscarProcedimientoEditarModal
                show={showBuscarModal}
                handleClose={() => setShowBuscarModal(false)}
                yaAgregados={form.detalles}
                onAgregar={handleAgregarExamenes}
            />
        </Modal>
    );
}
